import { ApolloError, useQuery } from '@apollo/client'
import { whereToBuyVariables, whereToBuy } from '../__generated__/whereToBuy'
import React, { createContext, FC, useEffect, useState } from 'react'
import { ICity, IImageTickerItem, IWorkHours } from '../components/types'
import { WHERE_TO_BUY } from '../queries/whereToBuyQuery'
import { SortOrder } from '../__generated__/globalTypes'
import { createCity } from '../lib/createCity'
import { createPartners } from '../lib/createPartners'

type Tab = 'list' | 'map'

interface IWheretoBuyProvider {
  cities: ICity[]
  loading: boolean
  error: ApolloError
  setFilters: React.Dispatch<React.SetStateAction<IWheretoBuyFilters>>
  filters: IWheretoBuyFilters
  activeTab: Tab
  setActiveTab: React.Dispatch<React.SetStateAction<Tab>>
  mapCenter: number[]
  setMapCenter: React.Dispatch<React.SetStateAction<number[]>>
  partners: IImageTickerItem[]
  workHours: IWorkHours[]
}

interface IWheretoBuyFilters {
  web: boolean
  insta: boolean
  store: boolean
}

export const WheretoBuyContext = createContext({} as IWheretoBuyProvider)

const WheretoBuyProvider: FC = ({ children }) => {
  const [filters, setFilters] = useState<IWheretoBuyFilters>({
    web: false,
    insta: false,
    store: false,
  })
  const [cities, setCities] = useState<ICity[]>()
  const [activeTab, setActiveTab] = useState<Tab>('list')
  const [mapCenter, setMapCenter] = useState([55.698394, 80.502091])

  useEffect(() => {
    filter(filters)
  }, [filters])

  const wtbVars: whereToBuyVariables = {
    where: {
      iblock: { code: { equals: 'shops' } },
      active: { equals: true },
    },
    partnersWhere: {
      active: { equals: true },
      iblock: { code: { equals: 'partners' } },
      sections: { some: { code: { equals: 'where-to-buy-partners' } } },
    },
    settingsPlusWhere: {
      OR: [{ code: { equals: 'UF_CONTACTS_WORK_HOURS' } }],
    },
    first: 999,
    partnersFirst: 999,
    order: { sort: SortOrder.asc },
  }

  const { data, loading, error } = useQuery<whereToBuy, whereToBuyVariables>(
    WHERE_TO_BUY,
    { variables: wtbVars },
  )

  const partners = createPartners(data?.partners?.edges)

  useEffect(() => {
    setCities(data?.wtb?.nodes?.map((node) => createCity(node)))
  }, [data])

  const filter = ({ web, insta, store }: IWheretoBuyFilters): void => {
    const initedCities = data?.wtb?.nodes
      ?.filter((node) => node.name.trim().length > 1)
      .map((node) => createCity(node))

    let newCities = []

    if (web) {
      initedCities.map((city) => {
        newCities.push({
          ...city,
          shops: city.shops.filter(
            (shop) =>
              shop.site && !shop.site.includes('https://www.instagram.com'),
          ),
        })
      })
    }

    if (insta) {
      initedCities.map((city) => {
        newCities.push({
          ...city,
          shops: city.shops.filter((shop) =>
            shop.site.includes('https://www.instagram.com'),
          ),
        })
      })
    }

    if (store) {
      initedCities.map((city) => {
        newCities.push({
          ...city,
          shops: city.shops.filter((shop) => shop.address),
        })
      })
    }

    if (!web && !store && !insta) {
      newCities = initedCities
    }

    setCities(newCities)
  }

  const hours = data.plus.filter(
    (plusItem) => plusItem.code === 'UF_CONTACTS_WORK_HOURS',
  )[0]?.values

  const workHours: IWorkHours[] = hours?.map((hour) => {
    const hourArr = hour.split('|')

    return {
      day: hourArr[0].trim(),
      hours: hourArr[1].trim(),
    }
  })

  return (
    <WheretoBuyContext.Provider
      value={{
        cities,
        loading,
        error,
        setFilters,
        filters,
        activeTab,
        setActiveTab,
        mapCenter,
        setMapCenter,
        partners,
        workHours,
      }}
    >
      {children}
    </WheretoBuyContext.Provider>
  )
}
export default WheretoBuyProvider
